import { CommonModule } from '@angular/common';
import { Component, computed, effect, EffectRef, input, model, OnDestroy, OnInit, output, signal, TemplateRef, ViewChild } from '@angular/core';
import { GENERAL_AGENT_SVG } from 'src/app/common/svgs';
import { SelectComponent } from 'src/app/@core/components/ui/select/select.component';
import { SelectConfig, SelectOption } from 'src/app/@core/components/ui/select/select.model';
import { LLM_PROVIDERS } from '../../../../models/model';
import { SanitizeHtmlPipe } from 'src/app/@shared/pipes/sanitizeHtml.pipe';

export interface AIModelProviderOption extends SelectOption {
    label: string;
    value: LLM_PROVIDERS;
    icon?: string;
    badge?: 'recommended' | 'popular' | 'new' | 'beta';
    provider: string;
    description?: string;
    performance?: 'fast' | 'balanced' | 'powerful';
    contextWindow?: string;
    model?: string;
}

export const AVAILABLE_MODELS: AIModelProviderOption[] = [
    {
        label: 'OpenAI',
        value: LLM_PROVIDERS.OPENAI,
        icon: GENERAL_AGENT_SVG.OPENAI,
        badge: 'recommended',
        provider: 'OpenAI',
        description: 'Best for quick responses',
        performance: 'powerful',
        contextWindow: '128K tokens'
    },
    {
        label: 'Anthropic',
        value: LLM_PROVIDERS.ANTHROPIC,
        icon: GENERAL_AGENT_SVG.ANTHROPIC,
        badge: 'popular',
        provider: 'Anthropic',
        description: 'Best for complex tasks',
        performance: 'balanced',
        contextWindow: '128K tokens'
    },
    {
        label: 'Google',
        value: LLM_PROVIDERS.GOOGLE,
        icon: GENERAL_AGENT_SVG.GOOGLE,
        provider: 'Google',
        description: 'Best for everyday tasks',
        performance: 'fast',
        contextWindow: '1M tokens'
    },
    // {
    //   label: 'xAI',
    //   value: LLM_PROVIDERS.XAI,
    //   icon: GENERAL_AGENT_SVG.GROK,
    //   provider: 'Grok',
    //   description: 'Most intelligent Claude model',
    //   performance: 'powerful',
    //   contextWindow: '200K tokens'
    // }
]

@Component({
    selector: 'app-model-selector',
    standalone: true,
    imports: [CommonModule, SelectComponent, SanitizeHtmlPipe],
    templateUrl: './model-selector.component.html',
    styleUrl: './model-selector.component.scss'
})
export class ModelSelectorComponent implements OnInit, OnDestroy {
    @ViewChild('itemTemplate') itemTemplate!: TemplateRef<any>;
    @ViewChild('selectedItemTemplate') selectedItemTemplate!: TemplateRef<any>;

    selectedModel = model<LLM_PROVIDERS>(LLM_PROVIDERS.ANTHROPIC);
    disabled = input<boolean>(false);
    placeholder = input<string>('Select AI Model');
    modelOptions = input<AIModelProviderOption[]>(AVAILABLE_MODELS);
    iconOnly = input<boolean>(false);

    modelChanged = output<AIModelProviderOption>();
    SVG_ICONS = GENERAL_AGENT_SVG

    selectConfig = computed<SelectConfig>(() => ({
        placeholder: this.placeholder(),
        searchable: false,
        searchPlaceholder: 'Search models...',
        backendSearch: false,
        clearable: false,
        disabled: this.disabled(),
        filter: true,
        filterMatchMode: 'contains',
        showClear: false,
        virtualScroll: false,
        panelStyleClass: 'model-selector-panel'
    }));

    private modelEffect?: EffectRef;

    constructor() {
        this.modelEffect = effect(() => {
            const currentModel = this.selectedModel();
            const modelOption = this.modelOptions().find(m => m.value === currentModel);
            if (modelOption) {
                this.modelChanged.emit(modelOption);
            }
        });
    }

    ngOnInit(): void {
        const initialModel = this.selectedModel();
        if (initialModel) {
            this.onModelChange({ value: initialModel });
        }
    }

    ngOnDestroy(): void {
        if (this.modelEffect) {
            this.modelEffect.destroy();
        }
    }

    onModelChange(event: any): void {
        const selectedValue = event.value;
        const model = this.modelOptions().find(m => m.value === selectedValue);
        if (model) {
            this.modelChanged.emit(model);
        }
    }

    isModelSelected(model: AIModelProviderOption): boolean {
        return model.value === this.selectedModel();
    }

    getBadgeTailwindClass(badge?: string): string {
        switch (badge) {
            case 'recommended':
                return 'bg-blue-100 text-blue-800';
            case 'popular':
                return 'bg-amber-100 text-amber-800';
            case 'new':
                return 'bg-emerald-100 text-emerald-800';
            case 'beta':
                return 'bg-indigo-100 text-indigo-800';
            default:
                return 'bg-gray-200 text-gray-700';
        }
    }

    getPerformanceIcon(performance?: string): string {
        switch (performance) {
            case 'fast':
                return 'pi pi-bolt';
            case 'balanced':
                return 'pi pi-sliders-h';
            case 'powerful':
                return 'pi pi-star-fill';
            default:
                return '';
        }
    }

    getPerformanceColor(performance?: string): string {
        switch (performance) {
            case 'fast':
                return '#10b981';
            case 'balanced':
                return '#6366f1';
            case 'powerful':
                return '#f59e0b';
            default:
                return '#3b82f6';
        }
    }
}
